import { formatDate } from "@/lib/utils";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Users,
  Store,
  Calendar,
  Layers,
  CheckCircle,
  Clock,
  Printer as PrintIcon,
  Package,
  Wrench,
  RefreshCcw,
  Loader2,
  Tag,
  ArrowUpDown,
  DollarSign,
  Box,
  Hash,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { DataTable } from "@/components/ui/data-table";

import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const PartsUsageReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    category_id: "",
    product_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    technician_id: "",
    start_date: "",
    end_date: "",
    usage_type: "all",
    sort_by: "usage_date",
    sort_order: "desc",
  }));
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");

  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCategorySearch(categorySearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [categorySearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearch(productSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ==================== QUERIES ====================
  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    enabled: hasPermission("report parts usage view"),
    queryKey: ["categories", debouncedCategorySearch],
    queryFn: async () => {
      const params = {};
      if (debouncedCategorySearch) {
        params.search = debouncedCategorySearch;
      }
      const response = await apiClient.get(`/category_product`, {
        params,
      });
      return response.data.data;
    },
  });

  const { data: products = [], isFetching: isProductsFetching } = useQuery({
    enabled: hasPermission("report parts usage view"),
    queryKey: ["products", debouncedProductSearch],
    queryFn: async () => {
      const params = {};
      if (debouncedProductSearch) {
        params.search = debouncedProductSearch;
      }
      const response = await apiClient.get(`/product`, {
        params,
      });
      return response.data.data;
    },
  });

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report parts usage view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  const { data: technicians = [] } = useQuery({
    enabled: hasPermission("report parts usage view"),
    queryKey: ["technicians"],
    queryFn: async () => {
      const response = await apiClient.get(`/user`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report parts usage view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.product_id)
        params.product_id = currentFilters.product_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.technician_id)
        params.technician_id = currentFilters.technician_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.usage_type)
        params.usage_type = currentFilters.usage_type;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.PARTS_USAGE,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report parts usage view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.product_id)
        params.product_id = currentFilters.product_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.technician_id)
        params.technician_id = currentFilters.technician_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.usage_type)
        params.usage_type = currentFilters.usage_type;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.PARTS_USAGE,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Parts usage report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate parts usage report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      category_id: "",
      product_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      technician_id: "",
      start_date: "",
      end_date: "",
      usage_type: "all",
      sort_by: "usage_date",
      sort_order: "desc",
    });
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `parts-usage-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: category.name,
  }));

  const productOptions = products.map((product) => ({
    value: String(product.id),
    label: product.name,
  }));

  const storeOptions = [
    { value: "", label: "All Stores" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  const technicianOptions = technicians.map((tech) => ({
    value: String(tech.id),
    label: tech?.employee?.full_name || tech.username,
  }));

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "Product Name",
        cell: ({ row }) => (
          <span className="font-medium text-primary">
            {row.original.product_name}
          </span>
        ),
      },
      {
        accessorKey: "product_sku",
        header: "SKU",
        cell: ({ row }) => row.original.product_sku || "-",
      },
      {
        accessorKey: "usage_date",
        header: "Usage Date",
        cell: ({ row }) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.original.usage_date
              ? formatDate(row.original.usage_date)
              : "-"}
          </span>
        ),
      },
      {
        accessorKey: "usage_type",
        header: "Type",
        cell: ({ row }) => (
          <span className="capitalize">{row.original.usage_type || "-"}</span>
        ),
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        cell: ({ row }) => row.original.quantity || "-",
      },
      {
        accessorKey: "unit_cost",
        header: "Unit Cost",
        cell: ({ row }) => (
          <span>
            {currencySymbol}
            {parseFloat(row.original.unit_cost || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
        ),
      },
      {
        accessorKey: "total_cost",
        header: "Total Cost",
        cell: ({ row }) => (
          <span className="font-bold">
            {currencySymbol}
            {parseFloat(row.original.total_cost || 0).toLocaleString(
              undefined,
              { minimumFractionDigits: 2 },
            )}
          </span>
        ),
      },
      {
        accessorKey: "store_name",
        header: "Store",
        cell: ({ row }) => row.original.store_name || "-",
      },
      {
        accessorKey: "customer_name",
        header: "Customer",
        cell: ({ row }) => row.original.customer_name || "-",
      },
      {
        accessorKey: "technician_name",
        header: "Technician",
        cell: ({ row }) => row.original.technician_name || "-",
      },
    ],
    [currencySymbol],
  );

  const summary = previewData?.summary;

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Wrench className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Resource Tracking
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Parts Usage Report
              </span>
            </div>
          }
          subtitle="Monitor how spare parts and components are being utilized across repairs and sales for better procurement planning."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report parts usage view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={() => {
                showToast("Generating PDF report...", "info");
                generateReportMutation.mutate(filters);
              }}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report parts usage view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-4">
        {/* Main Filter Panel */}
        <Card className="border border-border/50 shadow-none overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Report Configuration</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Define the timeframe and resource scope
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-5">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Date Scope */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Timeline
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">From</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">To</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t space-y-3">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <ArrowUpDown className="h-4 w-4 text-primary" />{" "}
                      Sequencing
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Sort By</Label>
                        <Select
                          value={filters.sort_by}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_by: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30">
                            <SelectValue placeholder="Usage Date" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="usage_date">
                              Usage Date
                            </SelectItem>
                            <SelectItem value="product_name">
                              Part Name
                            </SelectItem>
                            <SelectItem value="quantity">Qty Used</SelectItem>
                            <SelectItem value="usage_type">
                              Category Type
                            </SelectItem>
                            <SelectItem value="total_cost">
                              Total Valuation
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Order</Label>
                        <Select
                          value={filters.sort_order}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_order: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">
                              Ascending (Oldest)
                            </SelectItem>
                            <SelectItem value="desc">
                              Descending (Newest)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entity Filters */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" /> Part Metadata
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Part Category
                      </Label>
                      <ReactSelect
                        options={categoryOptions}
                        isLoading={isCategoriesFetching}
                        value={categoryOptions.find(
                          (opt) => opt.value === String(filters.category_id),
                        )}
                        onInputChange={(val, { action }) => {
                          if (action === "input-change") {
                            setCategorySearch(val);
                          }
                        }}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            category_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Categories"
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Specific Part
                      </Label>
                      <ReactSelect
                        options={productOptions}
                        isLoading={isProductsFetching}
                        value={productOptions.find(
                          (opt) => opt.value === String(filters.product_id),
                        )}
                        onInputChange={(val, { action }) => {
                          if (action === "input-change") {
                            setProductSearch(val);
                          }
                        }}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            product_id: opt?.value || "",
                          }))
                        }
                        placeholder="Search for a specific part"
                        isClearable
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Consumption Context
                      </Label>
                      <Select
                        value={filters.usage_type}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, usage_type: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            Comprehensive (All)
                          </SelectItem>
                          <SelectItem value="repair">
                            Workshop Repairs
                          </SelectItem>
                          <SelectItem value="sale">Direct POS Sales</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" /> Accountability
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Technician
                      </Label>
                      <ReactSelect
                        options={technicianOptions}
                        value={technicianOptions.find(
                          (opt) => opt.value === String(filters.technician_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            technician_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Technicians"
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Location</Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Stores"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>

                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 relative overflow-hidden group mt-4">
                      <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                        <Layers className="h-16 w-16 text-primary" />
                      </div>
                      <h4 className="text-primary font-bold text-sm mb-2 relative z-10">
                        Usage Analytics
                      </h4>
                      <p className="text-primary/70 text-[11px] leading-relaxed relative z-10">
                        This report calculates total cost and quantity consumed
                        based on system logs from both repair work-orders and
                        retail invoices.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* KPI Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Parts Used
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {summary.total_parts || 0}
                      </h3>
                    </div>
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                      <Hash className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Cost
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(parseFloat(summary.total_cost) || 0).toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2 },
                        )}
                      </h3>
                    </div>
                    <div className="p-3 bg-rose-100 text-rose-600 rounded-xl">
                      <DollarSign className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Unique Products
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {summary.unique_products || 0}
                      </h3>
                    </div>
                    <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                      <Box className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="border-none shadow-lg ring-1 ring-border/50 overflow-hidden">
              <CardContent className="p-0">
                <DataTable
                  columns={columns}
                  data={previewData.data || []}
                  searchKey="product_name"
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Prepared!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The parts usage audit is finalized and ready for your review.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
